import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Fade from 'react-reveal/Fade';

import { LinkContext } from "../../LinkProvider";

const accounts = [
  {
    id: "classic",
    navKey: "accounts.nav.classic",
    titleKey: "accounts.classic.title",
    subtitleKey: "accounts.classic.subtitle",
    descKey: "accounts.classic.desc",
    features: [
      "accounts.classic.features.1",
      "accounts.classic.features.2",
      "accounts.classic.features.3",
      "accounts.classic.features.4",
      "accounts.classic.features.5",
      "accounts.classic.features.6",
    ],
  },
  {
    id: "silver",
    navKey: "accounts.nav.silver",
    titleKey: "accounts.silver.title",
    subtitleKey: "accounts.silver.subtitle",
    descKey: "accounts.silver.desc",
    features: [
      "accounts.silver.features.1",
      "accounts.silver.features.2",
      "accounts.silver.features.3",
      "accounts.silver.features.4",
      "accounts.silver.features.5",
      "accounts.silver.features.6",
      "accounts.silver.features.7",
      "accounts.silver.features.8",
    ],
  },
  {
    id: "gold",
    navKey: "accounts.nav.gold",
    titleKey: "accounts.gold.title",
    subtitleKey: "accounts.gold.subtitle",
    descKey: "accounts.gold.desc",
    features: [
      "accounts.gold.features.1",
      "accounts.gold.features.2",
      "accounts.gold.features.3",
      "accounts.gold.features.4",
      "accounts.gold.features.5",
      "accounts.gold.features.6",
      "accounts.gold.features.7",
      "accounts.gold.features.8",
    ],
  },
  {
    id: "platinum",
    navKey: "accounts.nav.platinum",
    titleKey: "accounts.platinum.title",
    subtitleKey: "accounts.platinum.subtitle",
    descKey: "accounts.platinum.desc",
    features: [
      "accounts.platinum.features.1",
      "accounts.platinum.features.2",
      "accounts.platinum.features.3",
      "accounts.platinum.features.4",
      "accounts.platinum.features.5",
      "accounts.platinum.features.6",
      "accounts.platinum.features.7",
      "accounts.platinum.features.8",
    ],
  },
  {
    id: "vip",
    navKey: "accounts.nav.vip",
    titleKey: "accounts.vip.title",
    subtitleKey: "accounts.vip.subtitle",
    descKey: "accounts.vip.desc",
    features: [
      "accounts.vip.features.1",
      "accounts.vip.features.2",
      "accounts.vip.features.3",
      "accounts.vip.features.4",
      "accounts.vip.features.5",
      "accounts.vip.features.6",
      "accounts.vip.features.7",
      "accounts.vip.features.8",
    ],
  },
];

function AccountBlock({ acc, t, link, blockRef, withAnchor = false }) {
  return (
    <div
      ref={blockRef}
      id={withAnchor ? acc.id : undefined}
      className="scroll-mt-[130px] flex flex-col gap-[20px]"
    >
      <h2 className="font-['Nunito_Sans'] text-[26px] xl:text-[32px] font-black leading-[1.5] text-[#3273b4]">
        {t(acc.titleKey)}
      </h2>
      <span className="block h-[4px] w-[104px] bg-[#05193a]" />
      <div className="flex flex-col gap-[15px]">
        <p className="font-['Nunito_Sans'] text-[18px] xl:text-[21px] font-black leading-[1.5] text-[#05193a]">
          {t(acc.subtitleKey)}
        </p>
        <p className="font-['Nunito_Sans'] text-[14px] xl:text-[16px] font-light leading-[1.5] text-[#05193a]">
          {t(acc.descKey)}
        </p>
      </div>
      <div className="flex flex-col gap-[15px]">
        <p className="font-['Nunito_Sans'] text-[18px] xl:text-[21px] font-black leading-[1.5] text-[#05193a]">
          {t("accounts.keyFeatures")}
        </p>
        <div className="flex flex-wrap gap-x-[30px] gap-y-[10px]">
          {acc.features.map((fk) => (
            <div key={fk} className="flex items-center gap-[6px]">
              <span className="h-[5px] w-[5px] shrink-0 rounded-full bg-[#05193a]" />
              <span className="font-['Nunito_Sans'] text-[14px] xl:text-[16px] font-light leading-[1.5] text-[#05193a]">
                {t(fk)}
              </span>
            </div>
          ))}
        </div>
      </div>
      <a
        href={link}
        className="mt-[10px] inline-flex h-[38px] xl:h-[42px] w-fit items-center justify-center rounded-[10px] bg-[#05193a] px-[28px] xl:px-[35px] font-[Inter] text-[14px] xl:text-[16px] font-light capitalize text-white transition-colors hover:bg-[#0d2c60]"
      >
        {t("header.openAccount")}
      </a>
    </div>
  );
}

function AccountTypes() {
  const { link } = useContext(LinkContext);
  const { t } = useTranslation();
  const [active, setActive] = useState("classic");
  const accountRefs = useRef({});

  useEffect(() => {
    const targets = accounts
      .map((acc) => accountRefs.current[acc.id])
      .filter(Boolean);

    if (!targets.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible?.target?.id) {
          setActive(visible.target.id);
        }
      },
      {
        root: null,
        rootMargin: "-130px 0px -65% 0px",
        threshold: [0.15, 0.3, 0.6],
      }
    );

    targets.forEach((target) => observer.observe(target));

    return () => observer.disconnect();
  }, []);

  const scrollToAccount = (id) => {
    const target = accountRefs.current[id];

    if (!target) return;

    setActive(id);
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="mt-[80px] xl:mt-[160px]">
      {/* Desktop */}
      <Fade bottom>
      <div className="hidden xl:block mx-auto max-w-[1220px]">
        <div className="flex gap-[84px]">
          {/* Sidebar */}
          <div className="shrink-0 w-[195px] flex flex-col sticky top-[130px] self-start">
            {accounts.map((acc, i) => {
              const isActive = active === acc.id;

              return (
              <button
                key={acc.id}
                type="button"
                onClick={() => scrollToAccount(acc.id)}
                className="flex items-start gap-[10px] text-left"
              >
                <div className="flex flex-col items-center">
                  <div
                    className={`mt-[4px] size-[12px] rounded-full ${
                      isActive ? "bg-[#3273b4]" : "border border-[#05193a] bg-white"
                    }`}
                  />
                  {i < accounts.length - 1 && (
                    <div className="h-[34px] w-px bg-[#d3d3d3]" />
                  )}
                </div>
                <p
                  className={`font-['Nunito_Sans'] text-[20px] leading-[1.5] text-[#05193a] ${
                    isActive ? "font-black" : "font-light"
                  }`}
                >
                  {t(acc.navKey)}
                </p>
              </button>
            );
            })}
          </div>

          {/* All accounts stacked */}
          <div className="flex flex-col gap-[80px] flex-1">
            {accounts.map((acc) => (
              <AccountBlock
                key={acc.id}
                acc={acc}
                t={t}
                link={link}
                withAnchor
                blockRef={(node) => {
                  accountRefs.current[acc.id] = node;
                }}
              />
            ))}
          </div>
        </div>
      </div>
      </Fade>

      {/* Mobile */}
      <Fade bottom>
      <div className="xl:hidden px-[20px]">
        {/* Tab pills */}
        <div className="flex flex-wrap gap-[15px] justify-center">
          {accounts.map((acc) => (
            <button
              key={acc.id}
              type="button"
              onClick={() => setActive(acc.id)}
              className={`px-[15px] py-[6px] rounded-[60px] font-['Nunito_Sans'] text-[14px] leading-[1.1] text-[#05193a] transition-colors ${
                active === acc.id
                  ? "bg-[#eaf4ff]"
                  : "border border-[rgba(5,25,58,0.5)]"
              }`}
            >
              {t(acc.navKey).replace(" account", "").replace(" Account", "")}
            </button>
          ))}
        </div>

        {/* Active account */}
        <div className="mt-[40px]">
          {accounts
            .filter((a) => a.id === active)
            .map((acc) => (
              <AccountBlock key={acc.id} acc={acc} t={t} link={link} />
            ))}
        </div>
      </div>
      </Fade>
    </section>
  );
}

export default AccountTypes;
